import { EditOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { removeEmptyOrNull } from 'library/helpers/utility';
import { useManagePermittedRoles } from 'library/hooks';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectFloorsByFloorID } from 'reduxStore/floors/selectors';
import { floorsActions } from 'reduxStore/floors/slice';
import { UploadBox } from 'zComponents/atoms';
import { Button, Checkbox, Flex, Form, Input, InputNumber, Modal, Tooltip, Typography } from 'zui/Antd';

const { Text, Title } = Typography;

const UpdateFloorModal = ({ children, open = false, setOpen, floorID }) => {
	// Hooks
	const [form] = Form.useForm();
	const {
		token: { fontWeightStrong, paddingXL, colorText, colorTextLabel, fontSizeSM }
	} = theme.useToken();

	// Refs
	const uploadRef = useRef(null);
	const initialValuesRef = useRef(null);

	// Redux
	const dispatch = useDispatch();
	const floorInfo = useSelector((state) => selectFloorsByFloorID(state, floorID));
	const { url: photoUrl } = floorInfo || {};
	const { updateFloorLoading: loading } = useSelector((state) => state.Floors);

	// Local State
	const [imagePath, setImagePath] = useState(null);
	const [image, setImage] = useState(null);
	const [imageChanged, setImageChanged] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);

	// Form Watch
	const watchedValues = Form.useWatch([], form);

	// Role-based permissions
	const isPermittedUpdateFloor = useManagePermittedRoles('updateFloor', {
		101: [floorInfo?.orgID],
		103: [floorInfo?.siteID],
		104: [floorInfo?.floorID]
	});

	// Populate form when modal opens
	useEffect(() => {
		if (open && floorInfo) {
			const initialValues = {
				name: floorInfo.name || '',
				xDimension: floorInfo.dimensionX || '',
				yDimension: floorInfo.dimensionY || ''
			};

			form.setFieldsValue({ ...initialValues, url: floorInfo.url || '' });
			setImagePath(floorInfo.url || '');
			setImage(floorInfo.url || '');
			initialValuesRef.current = initialValues;
			setHasChanges(false);
		}
	}, [floorInfo, form, open]);

	// Track form changes to enable Save Changes button
	useEffect(() => {
		if (!watchedValues || !initialValuesRef.current) return;
		const { name, xDimension, yDimension } = watchedValues;
		const { name: initName, xDimension: initX, yDimension: initY } = initialValuesRef.current;

		const formChanged =
			name !== initName || Number(xDimension) !== Number(initX) || Number(yDimension) !== Number(initY);

		setHasChanges(formChanged || imageChanged);
	}, [watchedValues, imageChanged]);

	// Close modal and reset
	const handleCancel = () => {
		form.resetFields();
		setOpen(false);
		setImageChanged(false);
	};

	// Submit form and dispatch update
	const handleFinish = async (values) => {
		await form.validateFields();

		const payload = {
			name: values.name,
			dimensionX: values.xDimension ? Number(values.xDimension) : null,
			dimensionY: values.yDimension ? Number(values.yDimension) : null,
			url: imagePath,
			mapID: floorInfo.mapID || null,
			siteID: floorInfo.siteID
		};

		dispatch(floorsActions.updateFloor({ floorID, data: removeEmptyOrNull(payload) }));
		handleCancel();
	};

	return (
		<Fragment>
			<Tooltip title={isPermittedUpdateFloor ? 'Update Floor' : "You don't have permission"}>
				{children ? (
					children
				) : (
					<Button
						type='primary'
						icon={<EditOutlined />}
						onClick={() => setOpen(true)}
						disabled={!isPermittedUpdateFloor}
					>
						Update Floor
					</Button>
				)}
			</Tooltip>

			<Modal
				open={open}
				onCancel={handleCancel}
				centered
				destroyOnClose
				width={683}
				footer={[
					<Button onClick={handleCancel}>Cancel</Button>,
					<Button type='primary' htmlType='submit' form='floorForm' loading={loading} disabled={!hasChanges}>
						Save Changes
					</Button>
				]}
				styles={{ content: { padding: paddingXL }, footer: { marginTop: 48 } }}
			>
				{/* Modal Header */}
				<Flex vertical style={{ marginBottom: paddingXL }}>
					<Title level={4} style={{ margin: 0, color: colorText }}>
						Update Floor
					</Title>
					<Text style={{ color: colorTextLabel, fontSize: fontSizeSM }}>Update your floor details</Text>
				</Flex>

				{/* Floor Form */}
				<Form
					id='floorForm'
					form={form}
					layout='vertical'
					onFinish={handleFinish}
					requiredMark={false}
					name='floorForm'
					key={floorID}
				>
					<Flex vertical gap={paddingXL}>
						{/* Floor Name */}
						<Form.Item
							name='name'
							label={<Text style={{ fontWeight: fontWeightStrong }}>Floor Name</Text>}
							style={{ marginBottom: 0 }}
							rules={[{ required: true, message: 'Please enter a floor name' }]}
						>
							<Input placeholder='eg. Level 1' />
						</Form.Item>

						{/* X/Y Dimensions */}
						<Flex gap={16}>
							<Form.Item
								name='xDimension'
								label={
									<Text style={{ color: colorTextLabel }} strong>
										X Dimension
									</Text>
								}
								style={{ width: '100%', marginBottom: 0 }}
								dependencies={['yDimension', 'url']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const y = getFieldValue('yDimension');
											if ((y || imagePath) && !value) {
												return Promise.reject(new Error('X Dimension is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<InputNumber placeholder='eg. 20' style={{ width: '100%' }} min={0} step={1} />
							</Form.Item>

							<Form.Item
								name='yDimension'
								label={
									<Text style={{ color: colorTextLabel }} strong>
										Y Dimension
									</Text>
								}
								style={{ width: '100%', marginBottom: 0 }}
								dependencies={['xDimension', 'url']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const x = getFieldValue('xDimension');
											if ((x || imagePath) && !value) {
												return Promise.reject(new Error('Y Dimension is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<InputNumber placeholder='eg. 24.5' style={{ width: '100%' }} min={0} step={1} />
							</Form.Item>
						</Flex>

						{/* Upload Floor Plan */}
						<Flex vertical gap={16}>
							<Form.Item
								label={<Text style={{ fontWeight: fontWeightStrong }}>Floor Plan (Optional)</Text>}
								name='url'
								style={{ marginBottom: 0 }}
								dependencies={['xDimension', 'yDimension']}
								rules={[
									({ getFieldValue }) => ({
										validator(_, value) {
											const x = getFieldValue('xDimension');
											const y = getFieldValue('yDimension');
											if ((x || y) && !imagePath) {
												return Promise.reject(new Error('Floor Plan is required'));
											}
											return Promise.resolve();
										}
									})
								]}
							>
								<UploadBox
									entityID={3}
									photoUrl={photoUrl || null}
									entityName='Floor'
									shape='square'
									cursor
									edit
									size={161}
									uploadRef={uploadRef}
									setImage={setImage}
									image={image}
									setImagePath={(path) => {
										setImagePath(path);
										setImageChanged(path !== photoUrl);
									}}
									imagePath={imagePath}
									placeHolderUpload='Drag and drop or click to update the floor plan'
								/>
							</Form.Item>
						</Flex>

						{/* Waiver checkbox if image changed */}
						{imageChanged && (
							<Flex align='start' gap={8}>
								<Form.Item
									name='confirmDisassociate'
									valuePropName='checked'
									style={{ margin: 0 }}
									rules={[
										{
											type: 'boolean',
											required: true,
											message: 'Please confirm'
										}
									]}
									validateTrigger={['onChange', 'onBlur']}
								>
									<Flex gap={8} align='start'>
										<Checkbox />
										<Text>
											I understand that uploading a new floor plan will disassociate all devices
											currently associated with this map.
										</Text>
									</Flex>
								</Form.Item>
							</Flex>
						)}
					</Flex>
				</Form>
			</Modal>
		</Fragment>
	);
};

export default UpdateFloorModal;
